import { snapshotProject, applySnapshot } from './persistence.js';

// Undo/redo via project snapshots. We snapshot a JSON view of the project on
// each "checkpoint" event and walk that stack on Cmd+Z / Cmd+Shift+Z. Snapshots
// are debounced so a long drag doesn't flood the stack with every frame.
//
// We listen for explicit `history:checkpoint` events (UI emits these on
// commit-style actions) plus a few mixer/transport events. Avoid coupling
// directly to UI components — anything that wants to be undoable just emits
// `history:checkpoint`.

const CHECKPOINT_EVENTS = [
    'history:checkpoint',
    'pattern:cleared',
    'pattern:lengthChanged',
    'pattern:listChanged',
    'mixer:changed',
    'transport:bpmChanged',
];

const MAX_HISTORY = 100;

export function createHistory(ctx) {
    const stack = [];
    let pointer = -1;
    let suppressed = false;
    let timer = null;

    function applyAndSuppress(snap) {
        suppressed = true;
        try { applySnapshot(ctx, snap); } finally { suppressed = false; }
    }

    function pushNow() {
        const snap = snapshotProject(ctx);
        // Drop any redo branch if the user makes a fresh change after undoing.
        stack.length = pointer + 1;
        stack.push(snap);
        if (stack.length > MAX_HISTORY) stack.shift();
        pointer = stack.length - 1;
        ctx.bus.emit('history:changed', state());
    }

    function pushDebounced() {
        if (suppressed) return;
        clearTimeout(timer);
        timer = setTimeout(pushNow, 150);
    }

    function undo() {
        if (pointer <= 0) return;
        clearTimeout(timer);
        pointer -= 1;
        applyAndSuppress(stack[pointer]);
        ctx.bus.emit('history:changed', state());
    }

    function redo() {
        if (pointer >= stack.length - 1) return;
        clearTimeout(timer);
        pointer += 1;
        applyAndSuppress(stack[pointer]);
        ctx.bus.emit('history:changed', state());
    }

    function state() {
        return { canUndo: pointer > 0, canRedo: pointer < stack.length - 1 };
    }

    // Capture the initial snapshot so the first undo can restore it.
    ctx.bus.on('app:started', () => {
        // Wait one tick for default modules to settle.
        setTimeout(() => { pushNow(); }, 0);
        for (const ev of CHECKPOINT_EVENTS) ctx.bus.on(ev, pushDebounced);
    });

    return { undo, redo, state, pushNow };
}
